<script setup>
import { nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useBreakpoint } from '../../composables/useBreakpoint'

const props = defineProps({ open: { type: Boolean, default: false }, title: { type: String, default: 'Диалог' }, closeOnBackdrop: { type: Boolean, default: true } })
const emit = defineEmits(['request-close'])
const { isTabletUp } = useBreakpoint()
const panel = ref(null)
let previouslyFocused = null
let previousBodyOverflow = ''
const focusableSelector = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'
function requestClose() { emit('request-close') }
function focusFirstElement() { const first = panel.value?.querySelector(focusableSelector); if (first instanceof HTMLElement) first.focus(); else panel.value?.focus() }
function handleKeydown(event) {
  if (!props.open) return
  if (event.key === 'Escape') { event.preventDefault(); requestClose(); return }
  if (event.key !== 'Tab' || !panel.value) return
  const elements = [...panel.value.querySelectorAll(focusableSelector)].filter((element) => element instanceof HTMLElement && !element.hidden)
  if (!elements.length) { event.preventDefault(); panel.value.focus(); return }
  const first = elements[0]; const last = elements[elements.length - 1]
  if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
  else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
}
watch(() => props.open, async (open) => {
  if (open) { previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null; previousBodyOverflow = document.body.style.overflow; document.body.style.overflow = 'hidden'; await nextTick(); focusFirstElement() }
  else { document.body.style.overflow = previousBodyOverflow; await nextTick(); previouslyFocused?.focus?.() }
})
onBeforeUnmount(() => { document.body.style.overflow = previousBodyOverflow })
</script>
<template>
  <Teleport to="body">
    <Transition name="drawer-fade">
      <div v-if="open" class="fixed inset-0 z-50 flex bg-slate-950/45 backdrop-blur-[2px]" :class="isTabletUp ? 'justify-end' : 'items-end justify-center px-2'" @keydown="handleKeydown">
        <button type="button" class="absolute inset-0 h-full w-full cursor-default" tabindex="-1" aria-label="Закрыть окно" @click="closeOnBackdrop && requestClose()"></button>
        <section ref="panel" class="relative flex w-full flex-col overflow-hidden bg-white shadow-2xl shadow-slate-950/25 outline-none" :class="isTabletUp ? 'h-dvh max-w-[40rem] border-l border-app-border' : 'max-h-[94dvh] max-w-md rounded-t-[2rem]'" role="dialog" aria-modal="true" :aria-label="title" tabindex="-1">
          <div v-if="!isTabletUp" class="shrink-0 bg-white px-5 pb-1 pt-3"><div class="mx-auto h-1.5 w-12 rounded-full bg-slate-200"></div></div>
          <header class="flex shrink-0 items-center justify-between gap-3 border-b border-app-border px-5 py-3 sm:px-7 sm:py-4">
            <h2 class="text-lg font-black text-app-text">{{ title }}</h2>
            <button type="button" class="flex h-10 w-10 items-center justify-center rounded-app-control text-app-muted hover:bg-slate-100" aria-label="Закрыть" @click="requestClose"><svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="m6 6 12 12M18 6 6 18"/></svg></button>
          </header>
          <div class="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-[calc(env(safe-area-inset-bottom,0px)+1.25rem)] pt-4 sm:px-7 sm:pb-7 sm:pt-5"><slot /></div>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>
